import React, { useContext, useEffect, useState } from "react";
import { StoreContext } from "../context/StoreContext";
import CssBaseline from "@mui/material/CssBaseline";
import useMediaQuery from "@mui/material/useMediaQuery";
import Container from "@mui/material/Container";
import NewDrawerItems from "../menu/NewDrawerItems";
import MainNavBar from "./MainNavbar";
import { useStyles } from "../styles/MainContainerStyles";
import PageController from "../components/PageController";
import { getUserRole, getUserFromCache } from "../user/UserCommon";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import * as queries from "../graphql/queries";
import { graphql, graphqlOperation } from "../modules/AmplifyServices";
import { execReadByPK } from "../modules/DBService";
import { Outlet } from "react-router-dom";
import { useAtom } from "jotai";
import {
    mbxUserAtom,
    dashboardInfoAtom,
    showCompanySettingsModalAtom,
    companyMasterTz,
    isMbxUserAdmin,
    isMbxUserClient,
    isMbxFreeTrialUserAtom,
    displaySubscriptionBannerAtom,
    mainNavBarHeightAtom,
    allServicesAtom,
    allCategoriesAtom,
    allSkillsAtom,
    openFeatureGateDialogAtom
} from "../atoms/atoms";
import { companyLocationByCompany } from "../queries/ListBookingsQueries";
import { OUTLET_PAGE } from "../context/reducers";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle
} from "@mui/material";
import {
    conformationDialogOpenAtom,
    conformationDialogContentAtom,
    confirmationFunctionAtom,
    conformationDialogTitleAtom,
    conformationDialogSubmitButtonAtom,
    hideCancelButtonAtom,
    conformationDialogIsSubmittingAtom,
    warningIconInHeadingAtom,
    allUsersAtom,
    isLoadingUsersAtom,
    incrementLoadingUsersProgressAtom,
    allLocationsAtom
} from "../atoms/atoms";

import CompanySettingsModal from "../forms/CompanySettingsModal";
import WarningTriangleIcon from "../images/WarningTriangleIcon.svg";
import { DialogTheme } from "../styles/DialogTheme";
import LoadingButton from "@mui/lab/LoadingButton";
import MobileNavBar from "./mobile/MobileNavBar";
import { MobileNavBarTheme } from "../styles/MobileNavBarTheme";
import {
    openAvailabilityDialogAtom,
    openUserDialogAtom,
    userPageSnackbarAtom
} from "../atoms/usersTable";
import UserProfileModal from "../user/components/UserProfileModal";
import { useQuery } from "@tanstack/react-query";
import { getMasterTimezone } from "../utils/CalendarFunctions/timezoneFunctions";
import { usersForTable } from "../queries/UserQueries";
import { execReadBySortkey } from "../modules/DBService";
import NotificationSnackbar from "./NotificationSnackbar";
import { FlagsContext } from "../context/flags";
import {
    serviceTypeByCompanyCustom,
    skillByCompanyCustom
} from "../queries/ProviderFormQueries";
import { serviceTypeCategoryByCompany } from "../queries/CustomQueries";
import { providersListAtom, showBookingDetailsAtom } from "../atoms/booking";
import { BookingDialog } from "../forms/BookingDialog";
import FeatureGateDialog from "./FeatureGateDialog";
import { AddEditServiceDialog } from "./AddEditServiceDialog";
import { openServiceDialogAtom } from "../atoms/servicesTable";
import AvailabilityModal from "../user/components/AvailabilityModal";

export default function MainContainer(props) {
    const { state } = useContext(StoreContext);
    const [openUserDialog, setOpenUserDialog] = useAtom(openUserDialogAtom);
    const [openAvailabilityDialog, setOpenAvailabilityDialog] = useAtom(
        openAvailabilityDialogAtom
    );
    const [showBookingDetails, setShowBookingDetails] = useAtom(
        showBookingDetailsAtom
    );
    const [mainNavBarHeight] = useAtom(mainNavBarHeightAtom);
    const [, setRole] = useState();
    const [loggedInUser, setLoggedInUser] = useState();
    const classes = useStyles({ mainNavBarHeight });
    const theme = useTheme();
    const featureFlags = useContext(FlagsContext);
    const { displayUpcomingMaintenanceMessage } = featureFlags || {};
    const { displayOngoingMaintenanceMessage } = featureFlags || {};
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });
    const open = props.open;
    const handleDrawerAction = () => {
        props.handleDrawerAction();
    };
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useAtom(
        conformationDialogOpenAtom
    );
    const [confirmationFunction] = useAtom(confirmationFunctionAtom);
    const [conformationDialogContent] = useAtom(conformationDialogContentAtom);
    const [conformationDialogTitle] = useAtom(conformationDialogTitleAtom);
    const [warningIconInHeading, setWarningIconInHeading] = useAtom(
        warningIconInHeadingAtom
    );
    const [conformationDialogSubmitButton] = useAtom(
        conformationDialogSubmitButtonAtom
    );
    const [hideCancelButton, setHideCancelButton] =
        useAtom(hideCancelButtonAtom);
    const [conformationDialogIsSubmitting] = useAtom(
        conformationDialogIsSubmittingAtom
    );
    const [userPageSnackbar, setUserPageSnackMessage] =
        useAtom(userPageSnackbarAtom);
    const {
        open: openSnackbar = false,
        message: snackMsg = "",
        severity: snackSeverity = "success"
    } = userPageSnackbar;
    const [mbxUser, setMbxUser] = useAtom(mbxUserAtom);
    const [isAdminLoggedIn] = useAtom(isMbxUserAdmin);
    const [userIsClient] = useAtom(isMbxUserClient);
    const [, setDashboardInfo] = useAtom(dashboardInfoAtom);
    const [showCompanySettingsModal, setShowCompanySettingsModal] = useAtom(
        showCompanySettingsModalAtom
    );
    const [, setAllUsers] = useAtom(allUsersAtom);
    const [, setAllLocations] = useAtom(allLocationsAtom);
    const [, setAllServices] = useAtom(allServicesAtom);
    const [, setAllSkills] = useAtom(allSkillsAtom);
    const [, setAllCategories] = useAtom(allCategoriesAtom);
    const { companyId } = mbxUser;
    const [, setCompanyMasterTz] = useAtom(companyMasterTz);
    const [, setIsLoadingUsers] = useAtom(isLoadingUsersAtom);
    const [, incrementLoadingUsersProgress] = useAtom(
        incrementLoadingUsersProgressAtom
    );
    const [isFreeTrial] = useAtom(isMbxFreeTrialUserAtom);
    const [, setDisplaySubscriptionBanner] = useAtom(
        displaySubscriptionBannerAtom
    );
    const [, setMainNavBarHeight] = useAtom(mainNavBarHeightAtom);
    const [, setProvidersList] = useAtom(providersListAtom);
    const [openFeatureGateDialog, setOpenFeatureGateDialog] = useAtom(
        openFeatureGateDialogAtom
    );
    const [openServiceDialog, setOpenServiceDialog] = useAtom(
        openServiceDialogAtom
    );

    const { isLoading: isLoadingUsers } = useQuery({
        queryKey: ["get-users"],
        queryFn: async () => {
            try {
                let providerResults = await execReadBySortkey({
                    opname: "userByCompany",
                    op: usersForTable,
                    id: {
                        companyId: companyId
                    },
                    skey: {
                        roleEmailaddress: {
                            beginsWith: {
                                role: "PROVIDER"
                            }
                        }
                    },
                    deleted: { ne: true },
                    filter: { deleted: { ne: true } },
                    limit: 500,
                    onEachCall: incrementLoadingUsersProgress
                });
                let adminResults = await execReadBySortkey({
                    opname: "userByCompany",
                    op: usersForTable,
                    id: {
                        companyId: companyId
                    },
                    skey: {
                        roleEmailaddress: {
                            beginsWith: {
                                role: "COMPANY_ADMIN_PROVIDER"
                            }
                        }
                    },
                    deleted: { ne: true },
                    filter: { deleted: { ne: true } },
                    limit: 500,
                    onEachCall: incrementLoadingUsersProgress
                });
                let managerResults = await execReadBySortkey({
                    opname: "userByCompany",
                    op: usersForTable,
                    id: {
                        companyId: companyId
                    },
                    skey: {
                        roleEmailaddress: {
                            beginsWith: {
                                role: "MANAGER_PROVIDER"
                            }
                        }
                    },
                    deleted: { ne: true },
                    filter: { deleted: { ne: true } },
                    limit: 500,
                    onEachCall: incrementLoadingUsersProgress
                });
                const users = [
                    ...adminResults.items,
                    ...providerResults.items,
                    ...managerResults.items
                ];
                return users || null;
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (userData) => {
            setAllUsers(userData);
        }
    });
    useQuery({
        queryKey: ["get-locations"],
        queryFn: async () => {
            try {
                let {
                    data: {
                        companyLocationByCompany: { items }
                    }
                } = await graphql(
                    graphqlOperation(companyLocationByCompany, {
                        companyId: companyId,
                        limit: 500
                    })
                );
                return items || null;
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (LocationsData) => {
            setAllLocations(LocationsData);
        }
    });
    useQuery({
        queryKey: ["get-services"],
        queryFn: async () => {
            try {
                let {
                    data: {
                        serviceTypeByCompany: { items }
                    }
                } = await graphql(
                    graphqlOperation(serviceTypeByCompanyCustom, {
                        companyId: companyId,
                        filter: {
                            deleted: { ne: true },
                            isBundledService: { ne: true }
                        },
                        limit: 500
                    })
                );
                return items || null;
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (servicesData) => {
            setAllServices(servicesData);
        }
    });
    useQuery({
        queryKey: ["get-skills"],
        queryFn: async () => {
            try {
                let {
                    data: {
                        skillByCompany: { items }
                    }
                } = await graphql(
                    graphqlOperation(skillByCompanyCustom, {
                        companyId: companyId,
                        filter: {
                            deleted: { ne: true }
                        },
                        limit: 500
                    })
                );
                return items || null;
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (SkillsData) => {
            setAllSkills(SkillsData);
        }
    });
    useQuery({
        queryKey: ["get-categories"],
        queryFn: async () => {
            try {
                let result = await execReadByPK({
                    opname: "serviceTypeCategoryByCompany",
                    op: serviceTypeCategoryByCompany,
                    id: { companyId },
                    filter: {
                        deleted: { ne: true }
                    },
                    sortDirection: "DESC"
                });
                return result || null;
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (categoriesData) => {
            setAllCategories(categoriesData.items);
        }
    });
    const { data: providers } = useQuery({
        queryKey: ["get-providers-list"],
        queryFn: async () => {
            try {
                let providersList = await execReadBySortkey({
                    opname: "providerByCompany",
                    op: /* GraphQL */ `
                        query ProviderByCompany(
                            $companyId: String
                            $firstnameLastnameCreatedAt: ModelProviderByCompanyCompositeKeyConditionInput
                            $sortDirection: ModelSortDirection
                            $filter: ModelProviderFilterInput
                            $limit: Int
                            $nextToken: String
                        ) {
                            providerByCompany(
                                companyId: $companyId
                                firstnameLastnameCreatedAt: $firstnameLastnameCreatedAt
                                sortDirection: $sortDirection
                                filter: $filter
                                limit: $limit
                                nextToken: $nextToken
                            ) {
                                items {
                                    id
                                    firstname
                                    lastname
                                    emailaddress
                                    phone
                                    timezone
                                    ratingstarsavg
                                    user {
                                        id
                                    }
                                }

                                nextToken
                            }
                        }
                    `,
                    id: {
                        companyId: mbxUser.companyId
                    },
                    filter: {
                        and: [
                            { active: { ne: false } },
                            { deleted: { ne: true } }
                        ]
                    }
                });

                if (
                    providersList &&
                    !providersList.error &&
                    providersList.items &&
                    providersList.items.length
                ) {
                    providersList.items.forEach((item) => {
                        item.name = `${item.firstname} ${item.lastname}`;
                        item.email = item.emailaddress;
                    });
                    return providersList.items;
                }
                return [];
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (providersList) => {
            setProvidersList(providersList);
        }
    });

    setIsLoadingUsers(isLoadingUsers);
    useQuery({
        queryKey: ["get-master-tz"],
        queryFn: async () => {
            try {
                const masterTz = await getMasterTimezone();
                return masterTz;
            } catch (e) {
                console.log(e);
            }
        },
        onSuccess: (masterTz) => {
            setCompanyMasterTz(masterTz);
        }
    });

    displaySubscriptionBanner();

    async function displaySubscriptionBanner() {
        if (isFreeTrial) {
            // Check if the logged-in user is an admin
            if (isAdminLoggedIn) {
                try {
                    // Query to get the admins, sorted by createdAt to get the first admin
                    let adminResults = await execReadBySortkey({
                        opname: "userByCompany",
                        op: usersForTable,
                        id: {
                            companyId: mbxUser.companyId
                        },
                        skey: {
                            roleEmailaddress: {
                                beginsWith: {
                                    role: "COMPANY_ADMIN_PROVIDER"
                                }
                            }
                        },
                        deleted: { ne: true },
                        filter: { deleted: { ne: true } },
                        limit: 500,
                        sortDirection: "ASC"
                    });

                    const firstAdmin = adminResults.items[0];

                    // Check if the logged-in admin is the first-ever admin
                    if (firstAdmin && firstAdmin.id === mbxUser.id) {
                        setDisplaySubscriptionBanner(true);
                        setMainNavBarHeight("144px");
                    } else {
                        // If logged-in admin is not the first admin, check maintenance flags
                        if (
                            displayUpcomingMaintenanceMessage ||
                            displayOngoingMaintenanceMessage
                        ) {
                            setMainNavBarHeight("144px");
                        } else {
                            setMainNavBarHeight("72px");
                        }
                    }
                } catch (error) {
                    console.error("Error fetching admin data:", error);
                    setMainNavBarHeight("72px");
                }
            } else {
                // If not an admin, check maintenance flags
                if (
                    displayUpcomingMaintenanceMessage ||
                    displayOngoingMaintenanceMessage
                ) {
                    setMainNavBarHeight("144px");
                } else {
                    setMainNavBarHeight("72px");
                }
            }
        } else {
            // If not a free trial, check maintenance flags
            if (
                displayUpcomingMaintenanceMessage ||
                displayOngoingMaintenanceMessage
            ) {
                setMainNavBarHeight("144px");
            } else {
                setMainNavBarHeight("72px");
            }
        }
    }

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }

        setUserPageSnackMessage({
            open: false,
            message: snackMsg,
            severity: snackSeverity
        });
    };

    useEffect(() => {
        async function getUserAndRole() {
            const r = await getUserRole();
            const user = await getUserFromCache();
            setLoggedInUser(user);
            setRole(r);
        }
        getUserAndRole();
    }, []);
    // fecthes companyData every 1 hour
    useEffect(() => {
        const fetchCompanyData = async () => {
            // Fetch companyData
            const result = await graphql(
                graphqlOperation(queries.getCompany, { id: mbxUser.companyId })
            );

            // Update dashboardInfo for Dashboard Analytics
            setDashboardInfo(
                JSON.parse(result?.data?.getCompany?.DashboardInfo)
            );

            // Handle expired mbxUser
            if (
                result &&
                result?.data?.getCompany &&
                !result?.data?.getCompany?.active
            ) {
                setMbxUser({
                    ...mbxUser,
                    company: {
                        ...mbxUser.company,
                        active: false
                    }
                });
            }
        };

        fetchCompanyData();
        const intervalId = setInterval(fetchCompanyData, 3600000);

        // clean up
        return () => clearInterval(intervalId);
    }, []);

    return (
        <div className={classes.root}>
            <CssBaseline />
            <MainNavBar
                handleSignout={null}
                handleDrawerAction={handleDrawerAction}
            />
            {isDesktop || userIsClient ? (
                <NewDrawerItems
                    handleDrawerAction={handleDrawerAction}
                    open={open}
                    subscriptionLevel={loggedInUser?.company?.subscriptionLevel}
                />
            ) : (
                <ThemeProvider theme={MobileNavBarTheme(theme)}>
                    <MobileNavBar />
                </ThemeProvider>
            )}
            <div className={classes.appBarSpacer} />
            <Container
                maxWidth={false}
                className={classes.containeropen}
                sx={{
                    maxWidth: "1440px",
                    paddingTop:
                        displayUpcomingMaintenanceMessage && "132px !important",
                    paddingLeft: userIsClient && "80px !important",
                    [theme.breakpoints.down("md")]: {
                        paddingTop:
                            displayUpcomingMaintenanceMessage &&
                            "180px !important"
                    }
                }}
            >
                {state.page === OUTLET_PAGE ? (
                    <Outlet />
                ) : (
                    <PageController newPage={state.page} />
                )}

                <CompanySettingsModal
                    open={showCompanySettingsModal}
                    onClose={() => setShowCompanySettingsModal(false)}
                />
            </Container>
            <ThemeProvider theme={DialogTheme(theme)}>
                <Dialog
                    open={confirmationDialogOpen}
                    maxWidth="sm"
                    minWidth="sm"
                    onClose={() => {
                        setConfirmationDialogOpen(false);
                        setWarningIconInHeading(false);
                        setHideCancelButton(false);
                    }}
                >
                    <DialogTitle variant="confirmationOrWarning">
                        {warningIconInHeading && (
                            <img
                                src={WarningTriangleIcon}
                                alt="Warning Triangle Icon"
                                style={{
                                    height: "24px",
                                    width: "24px"
                                }}
                            />
                        )}
                        {conformationDialogTitle}
                    </DialogTitle>
                    <DialogContent>{conformationDialogContent}</DialogContent>
                    <DialogActions
                        variant="confirmationOrWarning"
                        sx={{ gap: "10px" }}
                    >
                        {!hideCancelButton && (
                            <Button
                                variant="cancelButton"
                                disabled={conformationDialogIsSubmitting}
                                onClick={() => {
                                    setConfirmationDialogOpen(false);
                                    setWarningIconInHeading(false);
                                    setHideCancelButton(false);
                                }}
                            >
                                Cancel
                            </Button>
                        )}
                        <LoadingButton
                            variant="contained"
                            loading={conformationDialogIsSubmitting}
                            onClick={confirmationFunction.fn}
                        >
                            {conformationDialogSubmitButton}
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
                <UserProfileModal
                    open={openUserDialog}
                    onClose={() => setOpenUserDialog(false)}
                />
                <AvailabilityModal
                    open={openAvailabilityDialog}
                    onClose={() => {
                        setOpenAvailabilityDialog(false);
                    }}
                />
                <BookingDialog
                    open={showBookingDetails}
                    onClose={() => {
                        setShowBookingDetails(false);
                    }}
                />
                {openServiceDialog && (
                    <AddEditServiceDialog
                        open={openServiceDialog}
                        onClose={() => setOpenServiceDialog(false)}
                    />
                )}
            </ThemeProvider>
            <FeatureGateDialog
                open={openFeatureGateDialog}
                handleOnClose={() => setOpenFeatureGateDialog(false)}
            />
            <NotificationSnackbar
                open={openSnackbar}
                severity={snackSeverity}
                message={snackMsg}
                onClose={handleCloseSnackbar}
            />
            <main className={classes.content} />
        </div>
    );
}
